import MuiButton, { ButtonProps as MuiButtonProps } from '@mui/material/Button'
import clsx from 'clsx'
import type { JSX } from 'react'
import { ElementType } from 'react'

type AcceptedMuiProps<C extends ElementType> =
    // Omit href to enforce the use of routes
    Omit<MuiButtonProps<C, { component?: C }>, 'href' | 'sx'>

type CustomProps<C extends ElementType = 'button'> = {
    /**
     * Nudge the button to the left ('start') or right ('end') to align its text with content above or below.
     * Mainly intended for `variant=text`.
     *
     * @default undefined
     */
    edge?: 'start' | 'end'

    // Adding context on top of MUI's comment for this prop
    /**
     * 	Applies to both desktop and mobile.
     */
    fullWidth?: AcceptedMuiProps<C>['fullWidth']

    /**
     * 	If `true`, the button will take up the full width of its container. Only applies to mobile.
     *
     * @default false
     */
    fullWidthOnMobile?: boolean
}

export type ButtonProps<C extends ElementType = 'button'> = AcceptedMuiProps<C> & CustomProps

function Button<C extends ElementType = 'button'>(props: ButtonProps<C>): JSX.Element {
    const { fullWidthOnMobile, edge, className, ...otherProps } = props

    // MUI cannot infer which element a custom (non-string) `component` renders;
    // ours (e.g. <INLink>) render anchors, not native <button>s
    const nativeButton =
        otherProps.component === undefined || typeof otherProps.component === 'string' ? undefined : false

    return (
        <MuiButton
            variant="contained" // Differs from the MUI default value of `text`
            disableElevation // Differs from the MUI default value of `false`
            nativeButton={nativeButton}
            {...otherProps}
            className={clsx(
                edge === 'start' && 'edgeStart',
                edge === 'end' && 'edgeEnd',
                fullWidthOnMobile && 'fullWidthOnMobile',
                className
            )}
        />
    )
}

Button.displayName = 'DSButton'

export default Button
